import React from 'react'
import { ObjectFieldTemplateProps, ArrayFieldTemplateProps } from '@rjsf/utils'
import RjsfForm from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'
import { Form } from 'react-bootstrap'
import { Button } from '../Button/Button'
import { ReactComponent as TrashIcon } from '../../assets/images/trash.svg'

const objectFieldTemplate: React.FC<ObjectFieldTemplateProps> = ({ properties }) => {
  return (
    <div>
      {properties.map((prop) => {
        const propertiesContent = React.cloneElement(prop.content, {
          ...prop.content.props,
          name: undefined,
          schema: {
            ...prop.content.props.schema,
            title: undefined,
            description: undefined,
          },
        })

        const iconClassName = prop.content.props.schema?.icon

        return (
          <div key={prop.name} className='d-flex p-2 gap-3'>
            {iconClassName ? (
              <div className='flex-shrink-0 d-flex align-items-start pt-1 fs-5'>
                <i className={iconClassName} />
              </div>
            ) : null}
            <div className='flex-grow-1'>
              {prop.content.props.schema?.title ? (
                <div className='paragraph-medium bold pb-1'>{prop.content.props.schema.title}</div>
              ) : (
                <div className='paragraph-medium bold pb-1'>{prop.content.props.name}</div>
              )}
              {prop.content.props.schema?.description && <div className='paragraph-small pb-2'>{prop.content.props.schema.description}</div>}
              {propertiesContent}
            </div>
          </div>
        )
      })}
    </div>
  )
}

const fixedAgendaItemsArrayTemplate: React.FC<ArrayFieldTemplateProps> = (props) => {
  const { items, canAdd, onAddClick, disabled, readonly } = props

  return (
    <div>
      {items && items.length > 0 ? (
        <div className='d-flex flex-column gap-2 mb-3'>
          {items.map((item) => (
            <div key={item.key} className='d-flex align-items-start gap-2'>
              <div className='flex-grow-1'>{item.children}</div>
              {item.hasRemove && (
                <Button
                  variant='borderless'
                  onClick={item.onDropIndexClick(item.index)}
                  disabled={disabled || readonly}
                  className='p-0 mt-2'
                  aria-label={`Remove item ${item.index + 1}`}
                >
                  <TrashIcon />
                </Button>
              )}
            </div>
          ))}
        </div>
      ) : null}
      {canAdd ? (
        <div className='zocks-mt-1.25'>
          <Button
            variant='link'
            className='text-decoration-none p-0 zocks-text-primary-dark paragraph-small'
            onClick={onAddClick}
            disabled={disabled || readonly}
          >
            <i className='bi bi-plus-lg' />
            <span className='ms-1'>Add item</span>
          </Button>
        </div>
      ) : null}
    </div>
  )
}

export interface InputParameterSchemaConfigFormProps {
  inputParametersSchema: { [key: string]: any } | undefined
  inputParameters: { [key: string]: any }
  showCustomizationSetting: boolean
  customized: boolean
  onChange: (newInputParameters: { [key: string]: any }) => void
  onChangeCustomized?: () => void
  disabled: boolean
}

export const InputParameterConfigForm: React.FC<InputParameterSchemaConfigFormProps> = ({
  inputParametersSchema,
  inputParameters,
  showCustomizationSetting,
  customized,
  onChange,
  onChangeCustomized,
  disabled,
}) => {
  if (!inputParametersSchema) {
    return null
  }

  const disabledByCustomization = showCustomizationSetting && !customized

  return (
    <>
      {showCustomizationSetting ? (
        <div className='d-flex justify-content-end'>
          <Form.Check className='form-check' checked={customized} onChange={onChangeCustomized} label='Customize' disabled={disabled} />
        </div>
      ) : null}
      <RjsfForm
        disabled={disabled || disabledByCustomization}
        schema={inputParametersSchema}
        uiSchema={{
          'ui:submitButtonOptions': {
            norender: true,
          },
          'ui:ObjectFieldTemplate': objectFieldTemplate,
          template: {
            'ui:widget': 'textarea',
          },
          fixed: {
            'ui:ArrayFieldTemplate': fixedAgendaItemsArrayTemplate,
            'ui:options': {
              orderable: false,
            },
          },
          customInstructions: {
            'ui:widget': 'textarea',
          },
        }}
        validator={validator}
        formData={inputParameters}
        liveValidate={true}
        onChange={(e) => onChange(e.formData)}
        showErrorList={false}
      />
    </>
  )
}
